import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Loader2,
  PackageSearch,
  Plus,
  Search,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Disponibilita } from "@/components/m3/ComposizioneCarico";
import { EditorMail } from "@/components/m3/TestoAvvisoPannello";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  type ParametriMail,
  testoInviato,
  useAnteprimaTesto,
  useCommesseCandidate,
  useCommesseRighe,
  useCpCrea,
  useTrasportatori,
} from "@/hooks/useM3";
import { copia } from "@/lib/appunti";
import { formatPallet, palletRiga } from "@/lib/pallet";
import { cn } from "@/lib/utils";
import type { CaricoPrevistoNuovo, CommessaScelta, RigaComposizione } from "@/types/api";

export type Scelta = { escluso: boolean; colli: number | null };
type Spedizioniere =
  | { tipo: "nessuno" }
  | { tipo: "cliente" }
  | { tipo: "noto"; id: string; nome: string }
  | { tipo: "nuovo"; nome: string; email: string };

/** I pallet di una riga per i colli scelti: `null` = l'anagrafica non sa i pezzi
 *  per pallet (snack, merce acquistata); 0 = non occupa pallet (pallet EPAL,
 *  certificati: vedi le Impostazioni). */
function palletDi(r: RigaComposizione, colli: number | null, soglia: number): number | null {
  if (r.pallet_senza_dato) return null;
  if (r.pezzi_pallet == null) return 0;
  return palletRiga(colli, r.pezzi_pallet, soglia);
}

/**
 * «+ Nuovo carico» — il carico lo crea Marina (dal 01-10-2026 il modo
 * principale: le righe che Aida crea dalle mail erano incomplete, e Marina non
 * si fidava del tabellone).
 *
 * **Tre passi, altezza fissa** (secondo giro, Enrico: la finestra che cresceva
 * a ogni commessa aperta, e la mail che prendeva il posto del modulo senza
 * ritorno, non andavano):
 *  1. **la merce** — si cerca la commessa (cliente, numero o riferimento del
 *     cliente) e si scelgono le righe: parte spuntato solo ciò che è a
 *     magazzino, il resto si vede con la disponibilità e si spunta a mano;
 *  2. **i dati** — cliente, chi ritira, pallet, date, note, se si sanno;
 *  3. **la mail** — il testo proposto, modificabile, con la data prevista
 *     dell'ordine; le aggiunte di Marina vanno nelle Note.
 *
 * Il carico **si crea solo all'ultimo clic**: il testo del passo 3 è
 * un'anteprima (il server crea il carico in un savepoint e lo annulla), quindi
 * si può tornare indietro a correggere senza niente da sistemare sul tabellone.
 * «Aggiungi al tabellone» crea il carico senza mail da ogni passo.
 *
 * ⚠️ La ricerca sta su **M3** (`/m3/commesse-candidate`), non su `/m1/ordini`:
 * quella era dietro `modulo.M1`, che Marina non ha (403 travestito da «nessuna
 * commessa»). Da qui `error` si guarda.
 */
const PASSI = ["La merce", "I dati", "La mail"] as const;

/** Da dove parte il dialogo quando lo apre «Commesse pronte» (03-10-2026): il
 *  cliente, le sue commesse e le righe che Marina ha già spuntato lì. */
export type InizioNuovoCarico = {
  clienteNome: string;
  clienteCodice: number;
  commesse: { numero: number; riferimento: string | null }[];
  scelte: Record<string, Scelta>;
};

export function NuovoCaricoDialog({
  onClose,
  iniziale,
}: {
  onClose: () => void;
  iniziale?: InizioNuovoCarico;
}) {
  const [passo, setPasso] = useState(0);
  const [q, setQ] = useState("");
  const [commesse, setCommesse] = useState<CommessaScelta[]>(
    () => iniziale?.commesse.map((c) => ({ numero: c.numero, parziale: false })) ?? [],
  );
  const [riferimenti, setRiferimenti] = useState<Record<number, string | null>>(() =>
    Object.fromEntries((iniziale?.commesse ?? []).map((c) => [c.numero, c.riferimento])),
  );
  const [aperte, setAperte] = useState<Set<number>>(() => new Set(iniziale?.commesse.map((c) => c.numero)));
  // Le righe scelte altrove vincono sulla proposta del server (solo magazzino):
  // l'effetto qui sotto riempie solo le righe che non hanno ancora una scelta.
  const [scelte, setScelte] = useState<Record<string, Scelta>>(() => iniziale?.scelte ?? {});
  const [clienteNome, setClienteNome] = useState(iniziale?.clienteNome ?? "");
  const [clienteCodice, setClienteCodice] = useState<number | null>(iniziale?.clienteCodice ?? null);
  const [sped, setSped] = useState<Spedizioniere>({ tipo: "nessuno" });
  const [pallet, setPallet] = useState("");
  const [pronto, setPronto] = useState(false);
  const [prontoDal, setProntoDal] = useState("");
  const [dataRitiro, setDataRitiro] = useState("");
  const [note, setNote] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [parametri, setParametri] = useState<ParametriMail>({ lingua: "it", destinatario: "cliente" });
  const [creato, setCreato] = useState<{ oggetto: string } | null>(null);

  const crea = useCpCrea();
  const cercando = q.trim().length >= 2;
  const { data, isFetching, error } = useCommesseCandidate(q.trim(), cercando);
  const risultati = data?.items ?? [];
  const numeri = useMemo(() => commesse.map((c) => c.numero), [commesse]);
  const anteprima = useCommesseRighe(numeri);
  const perCommessa = useMemo(
    () => new Map((anteprima.data?.commesse ?? []).map((c) => [c.ordine_numero, c])),
    [anteprima.data],
  );
  const soglia = anteprima.data?.soglia_mezzo_pallet_pct ?? 50;
  // I pallet del carico, con la regola delle NDP, mentre Marina sceglie: le
  // righe senza dato in anagrafica si dicono, non si contano a zero.
  const conto = useMemo(() => {
    let totale = 0;
    const senza: string[] = [];
    const perNumero = new Map<number, number>();
    for (const c of anteprima.data?.commesse ?? []) {
      let tc = 0;
      for (const r of c.righe) {
        const sc = r.riga_id ? scelte[r.riga_id] : undefined;
        if (!sc || sc.escluso) continue;
        const p = palletDi(r, sc.colli, soglia);
        if (p == null) senza.push(r.descrizione ?? r.codice ?? "");
        else tc += p;
      }
      perNumero.set(c.ordine_numero, tc);
      totale += tc;
    }
    return { totale, senza, perNumero };
  }, [anteprima.data, scelte, soglia]);

  // La proposta di partenza arriva dal server (solo ciò che è a magazzino):
  // si prende per le righe nuove, senza toccare quelle che Marina ha già scelto.
  useEffect(() => {
    const nuove: Record<string, Scelta> = {};
    for (const c of anteprima.data?.commesse ?? []) {
      for (const r of c.righe) {
        if (r.riga_id && !(r.riga_id in scelte)) {
          nuove[r.riga_id] = { escluso: !!r.escluso, colli: r.colli };
        }
      }
    }
    if (Object.keys(nuove).length) setScelte((s) => ({ ...s, ...nuove }));
  }, [anteprima.data]); // eslint-disable-line react-hooks/exhaustive-deps

  function aggiungi(numero: number, nome: string | null, codice: number | null, rif: string | null) {
    if (commesse.some((c) => c.numero === numero)) return;
    setCommesse([...commesse, { numero, parziale: false }]);
    setRiferimenti((r) => ({ ...r, [numero]: rif }));
    // La commessa appena aggiunta si apre; le altre restano come Marina le ha
    // lasciate — aperte quante ne vuole (Enrico, 01-10: confrontarle serve).
    setAperte((a) => new Set(a).add(numero));
    // Il cliente lo sa già il sistema: solo la prima commessa lo imposta, così
    // una correzione a mano non viene sovrascritta.
    if (!clienteNome && nome) {
      setClienteNome(nome);
      setClienteCodice(codice);
    }
    setQ("");
  }

  const bozza: CaricoPrevistoNuovo = useMemo(
    () => ({
      cliente_nome: clienteNome.trim() || null,
      cliente_codice: clienteCodice,
      commesse,
      righe: (anteprima.data?.commesse ?? []).flatMap((c) =>
        c.righe
          .filter((r) => r.riga_id && scelte[r.riga_id])
          .map((r) => ({
            riga_id: r.riga_id as string,
            escluso: scelte[r.riga_id as string].escluso,
            colli: scelte[r.riga_id as string].colli,
          })),
      ),
      trasportatore_id: sped.tipo === "noto" ? sped.id : null,
      trasportatore_nome: sped.tipo === "nuovo" ? sped.nome.trim() || null : null,
      trasportatore_email: sped.tipo === "nuovo" ? sped.email.trim() || null : null,
      ritira_il_cliente: sped.tipo === "cliente",
      pallet: pallet.trim() ? Number(pallet) : null,
      pronto: pronto || !!prontoDal,
      pronto_dal: prontoDal || null,
      data_ritiro: dataRitiro || null,
      note: note.trim() || null,
    }),
    [clienteNome, clienteCodice, commesse, anteprima.data, scelte, sped, pallet, pronto, prontoDal, dataRitiro, note],
  );
  const senzaCliente = !clienteNome.trim() && commesse.length === 0;
  const testo = useAnteprimaTesto(passo === 2 && !senzaCliente ? bozza : null, parametri);

  function vai(n: number) {
    setErrore(null);
    if (n > passo && senzaCliente) {
      setErrore("Serve almeno una commessa, o il nome del cliente nel passo 2.");
      if (passo === 1 || n === 2) return;
    }
    setPasso(n);
  }

  function salva(conMail?: { oggetto: string; corpo: string }) {
    setErrore(null);
    if (senzaCliente) {
      setErrore("Serve almeno il cliente, o una commessa da cui ricavarlo.");
      return;
    }
    crea.mutate(
      {
        ...bozza,
        prepara_avviso: !!conMail,
        testo_mail: conMail ? testoInviato(conMail.corpo, parametri) : null,
      },
      {
        onSuccess: () => {
          if (conMail) setCreato({ oggetto: conMail.oggetto });
          else onClose();
        },
        onError: (e: unknown) =>
          setErrore(e instanceof Error ? e.message : "Non è stato possibile creare il carico."),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      {/* Altezza FISSA: la finestra non cambia misura aprendo una commessa o
          passando da un passo all'altro; scorre il contenuto. */}
      <DialogContent className="flex h-[88vh] flex-col gap-0 p-0 sm:max-w-3xl" data-testid="nuovo-carico">
        <div className="flex items-center gap-4 border-b px-6 py-4 pr-12">
          <DialogTitle className="text-base">Nuovo carico</DialogTitle>
          <ol className="ml-auto flex items-center gap-1 text-xs" aria-label="Passi">
            {PASSI.map((nome, n) => (
              <li key={nome} className="flex items-center gap-1">
                {n > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                <button
                  type="button"
                  disabled={!!creato}
                  onClick={() => vai(n)}
                  aria-current={passo === n ? "step" : undefined}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2.5 py-1",
                    passo === n ? "bg-brand-700 font-semibold text-white" : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  <span className="tabular-nums">{n + 1}</span> {nome}
                </button>
              </li>
            ))}
          </ol>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {passo === 0 && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="cerca-commessa"
                  aria-label="Cerca la commessa"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca per cliente, numero di commessa o riferimento del cliente…"
                  className="h-9 w-full rounded-md border bg-card pl-8 pr-8 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                {isFetching && (
                  <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
              </div>
              {cercando && (
                <div className="max-h-56 overflow-y-auto rounded-md border" data-testid="risultati-commesse">
                  {/* Una ricerca che non ha potuto cercare non dice «non ho
                      trovato»: lo diceva, ed è così che un 403 è passato per mesi
                      per «commessa non ancora nell'export». */}
                  {error ? (
                    <p className="flex items-start gap-2 px-3 py-3 text-xs text-red-700">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        La ricerca non ha risposto: {error instanceof Error ? error.message : "errore"}.
                        Non vuol dire che la commessa non esista — riprova, e se resta scrivi a Maitime.
                      </span>
                    </p>
                  ) : risultati.length === 0 ? (
                    <p className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground">
                      <PackageSearch className="h-3.5 w-3.5" />
                      Nessuna commessa per «{q.trim()}». Le più recenti possono non essere ancora
                      nell'export: in quel caso scrivi il cliente nel passo 2 e mettile in nota.
                    </p>
                  ) : (
                    risultati.map((o) => {
                      const scelta = commesse.some((c) => c.numero === o.ordine_numero);
                      return (
                        <button
                          key={o.ordine_numero}
                          type="button"
                          disabled={scelta}
                          onClick={() =>
                            aggiungi(o.ordine_numero, o.cliente_ragione_sociale, o.cliente_codice, o.riferimento_cliente ?? null)
                          }
                          className="flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-xs last:border-0 hover:bg-muted disabled:opacity-40"
                        >
                          <span className="min-w-0">
                            <span className="block truncate">
                              <span className="font-semibold tabular-nums">{o.ordine_numero}</span>
                              <span className="ml-2 text-muted-foreground">{o.cliente_ragione_sociale}</span>
                            </span>
                            <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-muted-foreground">
                              {/* Il numero con cui il cliente chiama la commessa: è
                                  quello che Marina ha davanti nelle sue mail. */}
                              {o.riferimento_cliente && (
                                <span className="font-medium text-foreground/80" data-testid="rif-candidata">
                                  rif. {o.riferimento_cliente}
                                </span>
                              )}
                              <span>
                                {o.righe} {o.righe === 1 ? "riga" : "righe"}
                              </span>
                              {o.data_consegna && (
                                <span>consegna {new Date(o.data_consegna).toLocaleDateString("it-IT")}</span>
                              )}
                              {o.su_altro_carico && (
                                <span className="rounded bg-red-100 px-1 py-0.5 text-[9.5px] font-medium text-red-800">
                                  già su un altro carico ({o.su_altro_carico})
                                </span>
                              )}
                            </span>
                          </span>
                          <span className="shrink-0 text-[10.5px] text-muted-foreground">
                            {scelta ? "già scelta" : `residua ${Math.round(o.qty_residua)}`}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              )}

              {commesse.length === 0 && !cercando && (
                <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
                  Cerca il cliente o la commessa. Senza commessa (un carico fatto a mano) passa
                  direttamente al passo 2 e scrivi il cliente.
                </p>
              )}
              <ul className="space-y-1.5">
                {commesse.map((c) => {
                  const ant = perCommessa.get(c.numero);
                  const righe = ant?.righe ?? [];
                  const incluse = righe.filter((r) => r.riga_id && !scelte[r.riga_id]?.escluso).length;
                  const espansa = aperte.has(c.numero);
                  return (
                    <li key={c.numero} className="rounded border bg-muted/20" data-commessa={c.numero}>
                      <div className="flex items-center gap-2 px-2 py-1.5 text-xs">
                        <button
                          type="button"
                          onClick={() =>
                            setAperte((a) => {
                              const n = new Set(a);
                              if (n.has(c.numero)) n.delete(c.numero);
                              else n.add(c.numero);
                              return n;
                            })
                          }
                          className="inline-flex items-center gap-1 font-semibold tabular-nums"
                          aria-expanded={espansa}
                        >
                          {espansa ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          {c.numero}
                        </button>
                        {riferimenti[c.numero] && (
                          <span className="truncate text-muted-foreground">rif. {riferimenti[c.numero]}</span>
                        )}
                        {anteprima.isLoading ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        ) : ant && ant.nell_export ? (
                          <span className="text-muted-foreground" data-testid="conteggio-righe">
                            {incluse} di {righe.length} righe ·{" "}
                            <span className="font-medium text-foreground">
                              {formatPallet(conto.perNumero.get(c.numero) ?? 0)} pallet
                            </span>
                          </span>
                        ) : (
                          // Senza righe nell'export non c'è niente da scegliere: resta
                          // la distinzione del foglio, intera o a stralcio.
                          <label className="flex items-center gap-1.5 text-muted-foreground">
                            <input
                              type="checkbox"
                              checked={!!c.parziale}
                              onChange={(e) =>
                                setCommesse(
                                  commesse.map((x) => (x.numero === c.numero ? { ...x, parziale: e.target.checked } : x)),
                                )
                              }
                            />
                            solo in parte (stralcio) · non ancora nell'export
                          </label>
                        )}
                        <button
                          type="button"
                          onClick={() => setCommesse(commesse.filter((x) => x.numero !== c.numero))}
                          className="ml-auto text-muted-foreground hover:text-red-600"
                          aria-label={`Togli la commessa ${c.numero}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {espansa && righe.length > 0 && (
                        <RigheCommessa
                          righe={righe}
                          soglia={soglia}
                          scelte={scelte}
                          onScelta={(id, sc) => setScelte((x) => ({ ...x, [id]: sc }))}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
              {commesse.length > 0 && <TotalePallet totale={conto.totale} senza={conto.senza} />}
            </div>
          )}

          {passo === 1 && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="cliente-nome">
                    Cliente
                  </label>
                  <input
                    id="cliente-nome"
                    value={clienteNome}
                    onChange={(e) => {
                      setClienteNome(e.target.value);
                      setClienteCodice(null); // scritto a mano = non più quello dell'anagrafica
                    }}
                    placeholder="Si compila con la prima commessa"
                    className="mt-1 h-9 w-full rounded-md border bg-card px-2.5 text-sm"
                  />
                </div>
                <SceltaSpedizioniere valore={sped} onChange={setSped} clienteCodice={clienteCodice} />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="pallet">
                    Pallet
                  </label>
                  <input
                    id="pallet"
                    type="number"
                    min="0"
                    value={pallet}
                    onChange={(e) => setPallet(e.target.value)}
                    placeholder={
                      conto.totale > 0 || conto.senza.length
                        ? `dalle righe: ${formatPallet(conto.totale)}${conto.senza.length ? " +" : ""}`
                        : "dalle righe"
                    }
                    className="mt-1 h-9 w-full rounded-md border bg-card px-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="pronto-dal">
                    Pronto dal
                  </label>
                  <input
                    id="pronto-dal"
                    type="date"
                    value={prontoDal}
                    onChange={(e) => setProntoDal(e.target.value)}
                    className="mt-1 h-9 w-full rounded-md border bg-card px-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="data-ritiro">
                    Ritiro confermato
                  </label>
                  <input
                    id="data-ritiro"
                    type="date"
                    value={dataRitiro}
                    onChange={(e) => setDataRitiro(e.target.value)}
                    className="mt-1 h-9 w-full rounded-md border bg-card px-2.5 text-sm"
                  />
                </div>
                {/* «Pronto» senza data è un caso legittimo: la merce è a magazzino e
                    la data non serve. Il flag esiste per poterlo dire. */}
                <label className="flex items-end gap-1.5 pb-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={pronto || !!prontoDal}
                    disabled={!!prontoDal}
                    onChange={(e) => setPronto(e.target.checked)}
                  />
                  pronto{prontoDal ? " (dalla data)" : ", data non nota"}
                </label>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground" htmlFor="note">
                  Note
                </label>
                <textarea
                  id="note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Orario, «manca 1 pallet»…"
                  className="mt-1 w-full rounded-md border bg-card px-2.5 py-1.5 text-sm"
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Con una data di ritiro il carico nasce «confermato», senza «in attesa».
              </p>
            </div>
          )}

          {passo === 2 &&
            (creato ? (
              <div className="space-y-3">
                <p className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" role="status">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Il carico è sul tabellone, segnato «mail preparata», e il testo è negli appunti.
                    Incollalo nella tua posta, copia qui sotto oggetto e indirizzi, e invialo con{" "}
                    <b>Aida in copia</b>: quando lo vedrà, aggiornerà il carico.
                  </span>
                </p>
                <CopieFinali
                  voci={[
                    ["A", (testo.data?.a ?? []).join("; ")],
                    ["Cc", (testo.data?.cc ?? []).join("; ")],
                    ["Oggetto", creato.oggetto],
                  ]}
                />
              </div>
            ) : (
              <EditorMail
                testo={testo.data}
                caricamento={testo.isLoading || testo.isFetching}
                errore={testo.error ? (testo.error as Error).message : null}
                parametri={parametri}
                onParametri={setParametri}
                haSpedizioniere={sped.tipo === "noto" || sped.tipo === "nuovo"}
                azione={{
                  etichetta: "Crea il carico e copia il testo",
                  inCorso: crea.isPending,
                  onClick: async (m) => {
                    // Si copia PRIMA di creare: dopo un'attesa di rete alcuni
                    // browser non considerano più il clic come gesto dell'utente,
                    // e rifiutano la scrittura negli appunti.
                    const ok = await copia(m.corpo);
                    if (!ok) setErrore("Il browser non ha permesso di copiare: lo potrai fare dal drawer.");
                    salva(m);
                  },
                }}
              />
            ))}
          {errore && <p className="mt-3 text-xs font-medium text-red-600">{errore}</p>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t px-6 py-3">
          {creato ? (
            <span />
          ) : passo > 0 ? (
            <Button variant="ghost" onClick={() => vai(passo - 1)}>
              <ChevronLeft className="mr-1 h-4 w-4" /> Indietro
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose}>
              Annulla
            </Button>
          )}
          <span className="flex flex-wrap gap-2">
            {creato ? (
              <Button onClick={onClose}>Chiudi</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => salva()} disabled={crea.isPending}>
                  <Plus className="mr-1.5 h-4 w-4" /> Aggiungi al tabellone senza mail
                </Button>
                {passo < 2 && (
                  <Button onClick={() => vai(passo + 1)}>
                    Avanti <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Dopo la creazione: oggetto e indirizzi da copiare, il testo è già negli appunti. */
function CopieFinali({ voci }: { voci: [string, string][] }) {
  const [fatto, setFatto] = useState<string | null>(null);
  return (
    <ul className="space-y-1.5 text-xs">
      {voci
        .filter(([, v]) => v)
        .map(([k, v]) => (
          <li key={k} className="flex items-center gap-2">
            <span className="w-14 shrink-0 text-muted-foreground">{k}</span>
            <span className="min-w-0 flex-1 truncate rounded border bg-muted/30 px-2 py-1.5">{v}</span>
            <button
              type="button"
              aria-label={`Copia ${k.toLowerCase()}`}
              onClick={async () => {
                if (await copia(v)) setFatto(k);
              }}
              className="rounded border p-1.5 hover:bg-muted"
            >
              {fatto === k ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </li>
        ))}
    </ul>
  );
}

/** Le righe di una commessa, da spuntare: colli presi e disponibilità. */
function RigheCommessa({
  righe,
  soglia,
  scelte,
  onScelta,
}: {
  righe: RigaComposizione[];
  soglia: number;
  scelte: Record<string, Scelta>;
  onScelta: (id: string, s: Scelta) => void;
}) {
  return (
    <ul className="divide-y border-t bg-card">
      {righe.map((r) => {
        const id = r.riga_id as string;
        const s = scelte[id] ?? { escluso: !!r.escluso, colli: r.colli };
        return (
          <li
            key={id}
            data-riga-articolo={r.codice ?? ""}
            className={cn("flex items-center gap-2 px-2 py-1.5 text-[11.5px]", s.escluso && "opacity-60")}
          >
            <input
              type="checkbox"
              aria-label={`Prendi ${r.codice ?? "la riga"}`}
              checked={!s.escluso}
              onChange={(e) =>
                onScelta(id, {
                  escluso: !e.target.checked,
                  // riaccendendo una riga spenta di partenza si prende tutta
                  colli: e.target.checked && !s.colli ? (r.colli_totali ?? null) : s.colli,
                })
              }
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate" title={r.descrizione ?? undefined}>
                {r.descrizione}
              </span>
              <span className="font-mono text-[10px] text-muted-foreground">{r.codice}</span>
            </span>
            <span className="flex items-center gap-1 tabular-nums">
              <input
                type="number"
                min="0"
                aria-label={`Colli di ${r.codice ?? "la riga"}`}
                disabled={s.escluso}
                value={s.colli ?? ""}
                max={r.colli_totali ?? undefined}
                onChange={(e) => {
                  const v = e.target.value === "" ? null : Number(e.target.value);
                  const tot = r.colli_totali ?? null;
                  onScelta(id, { escluso: false, colli: v != null && tot != null ? Math.min(v, tot) : v });
                }}
                className="h-7 w-16 rounded border bg-card px-1.5 text-right"
              />
              <span className="text-muted-foreground">/ {r.colli_totali ?? "—"}</span>
            </span>
            <PalletCella r={r} colli={s.escluso ? null : s.colli} soglia={soglia} />
            <span className="w-44 shrink-0 text-right">
              <Disponibilita riga={r} />
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** I pallet della riga, o perché non ci sono. */
function PalletCella({ r, colli, soglia }: { r: RigaComposizione; colli: number | null; soglia: number }) {
  const p = palletDi(r, colli, soglia);
  return (
    <span className="w-16 shrink-0 text-right tabular-nums" data-testid="pallet-riga">
      {r.pallet_senza_dato ? (
        <span className="text-[10.5px] text-amber-700" title="L'anagrafica non sa quanti pezzi stanno su un pallet">
          n.d.
        </span>
      ) : r.pezzi_pallet == null ? (
        <span className="text-[10.5px] text-muted-foreground" title="Non occupa pallet (vedi Impostazioni)">
          —
        </span>
      ) : colli == null ? (
        <span className="text-muted-foreground">·</span>
      ) : (
        <span title={`${r.pezzi_pallet} pezzi per pallet`}>{formatPallet(p ?? 0)} pal.</span>
      )}
    </span>
  );
}

/** Il totale dei pallet del carico, e gli articoli che non possiamo contare. */
function TotalePallet({ totale, senza }: { totale: number; senza: string[] }) {
  return (
    <p className="rounded-md border bg-muted/30 px-3 py-2 text-xs" data-testid="totale-pallet">
      Totale carico: <b className="tabular-nums">{formatPallet(totale)} pallet</b>
      {senza.length > 0 && (
        <span className="text-amber-800">
          {" "}
          più {senza.length === 1 ? "un articolo" : `${senza.length} articoli`} senza pezzi per
          pallet in anagrafica: {senza.slice(0, 3).join(", ")}
          {senza.length > 3 ? ` e altri ${senza.length - 3}` : ""}
        </span>
      )}
    </p>
  );
}

/** Chi ritira: uno dell'anagrafica (prima quelli che questo cliente usa già),
 *  il cliente stesso, o un nome nuovo che entra in anagrafica. */
function SceltaSpedizioniere({
  valore,
  onChange,
  clienteCodice,
}: {
  valore: Spedizioniere;
  onChange: (s: Spedizioniere) => void;
  clienteCodice: number | null;
}) {
  const [q, setQ] = useState("");
  const [aperto, setAperto] = useState(false);
  const { data: elenco } = useTrasportatori(q, clienteCodice);
  const trovati = (elenco ?? []).slice(0, 6);
  const etichetta =
    valore.tipo === "cliente"
      ? "Ritira il cliente"
      : valore.tipo === "noto"
        ? valore.nome
        : valore.tipo === "nuovo"
          ? `${valore.nome} (nuovo)`
          : null;

  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">Chi ritira</span>
      {etichetta && !aperto ? (
        <div className="mt-1 flex h-9 items-center justify-between rounded-md border bg-card px-2.5 text-sm">
          <span className="truncate">{etichetta}</span>
          <button
            type="button"
            onClick={() => onChange({ tipo: "nessuno" })}
            className="text-muted-foreground hover:text-red-600"
            aria-label="Togli chi ritira"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="relative mt-1">
          <input
            aria-label="Cerca lo spedizioniere"
            value={q}
            onFocus={() => setAperto(true)}
            onChange={(e) => {
              setQ(e.target.value);
              setAperto(true);
            }}
            placeholder="Spedizioniere (facoltativo)"
            className="h-9 w-full rounded-md border bg-card px-2.5 text-sm"
          />
          {aperto && (
            <div className="absolute z-10 mt-1 w-full rounded-md border bg-card shadow-md">
              <button
                type="button"
                onClick={() => {
                  onChange({ tipo: "cliente" });
                  setAperto(false);
                }}
                className="block w-full border-b px-2.5 py-1.5 text-left text-xs hover:bg-muted"
              >
                Ritira il cliente stesso
              </button>
              {trovati.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    onChange({ tipo: "noto", id: t.id, nome: t.nome });
                    setAperto(false);
                  }}
                  className="flex w-full items-center justify-between border-b px-2.5 py-1.5 text-left text-xs last:border-0 hover:bg-muted"
                >
                  <span className="truncate">{t.nome}</span>
                  {t.usi_cliente > 0 && (
                    <span className="text-[10px] text-muted-foreground">già usato {t.usi_cliente}×</span>
                  )}
                </button>
              ))}
              {q.trim() && !trovati.some((t) => t.nome.toLowerCase() === q.trim().toLowerCase()) && (
                <button
                  type="button"
                  onClick={() => {
                    onChange({ tipo: "nuovo", nome: q.trim(), email: "" });
                    setAperto(false);
                  }}
                  className="block w-full px-2.5 py-1.5 text-left text-xs font-medium text-brand-700 hover:bg-muted"
                >
                  + Aggiungi «{q.trim()}» agli spedizionieri
                </button>
              )}
              <button
                type="button"
                onClick={() => setAperto(false)}
                className="block w-full border-t px-2.5 py-1 text-right text-[10.5px] text-muted-foreground hover:bg-muted"
              >
                chiudi
              </button>
            </div>
          )}
        </div>
      )}
      {valore.tipo === "nuovo" && (
        <input
          aria-label="Email dello spedizioniere"
          type="email"
          value={valore.email}
          onChange={(e) => onChange({ ...valore, email: e.target.value })}
          placeholder="Email (facoltativa): lo riconosco da solo la prossima volta"
          className="mt-1 h-8 w-full rounded-md border bg-card px-2.5 text-xs"
        />
      )}
    </div>
  );
}
